import * as ThemeCart from "@shopify/theme-cart"
import {
  generateLineItemPropertyFromValue,
  magicDisable,
  magicEnable,
} from "./helpers"

const addToCartFormSelector = "form[action='/cart/add']"
const addToCartSelector = "[data-fsa-add-to-cart]"
const minusButtonSelector = "[data-fsa-minus]"
const visibleInputSelector = "[data-fsa-input]"
const plusButtonSelector = "[data-fsa-plus]"
const hiddenQuantitySelector = "[data-fsa-hidden-quantity]"
const hiddenLineItemPropSelector = "[data-fsa-hidden-line-item-props]"
const containerVariantIdSelector = (variantId) =>
  `[data-fsa-product-variant-id="${variantId}"]`

const getAllProductContainersByVariantId = (variantId) => {
  const containerSelector = containerVariantIdSelector(variantId)
  return document.querySelectorAll(containerSelector)
}

const getDataFrom = (container) => ({
  variantId: parseInt(container.dataset.fsaProductVariantId, 10),
  step: parseInt(container.dataset.fsaStep, 10),
  currentQuantity: parseInt(container.dataset.fsaCurrentQuantity, 10),
  minimum: parseInt(container.dataset.fsaMin, 10),
  maximum: parseInt(container.dataset.fsaMax, 10),
  multiplier: parseInt(container.dataset.fsaMultiplier, 10),
  shopifyMaximumQuantity: parseInt(container.dataset.fsaShopifyMax, 10),
  cartQuantity: parseInt(container.dataset.fsaCartQuantity, 10),
  itemKey: container.dataset.fsaCartItemKey,
})

const getAllElementsFrom = (container) => ({
  minusButton: container.querySelector(minusButtonSelector),
  visibleInput: container.querySelector(visibleInputSelector),
  plusButton: container.querySelector(plusButtonSelector),
  hiddenQuantity: container.querySelector(hiddenQuantitySelector),
  hiddenLIP: container.querySelector(hiddenLineItemPropSelector),
})

const getMinusPlusElementsFrom = (container) => ({
  minusButton: container.querySelector(minusButtonSelector),
  plusButton: container.querySelector(plusButtonSelector),
})

const getInputElementsFrom = (container) => ({
  visibleInput: container.querySelector(visibleInputSelector),
  hiddenQuantity: container.querySelector(hiddenQuantitySelector),
  hiddenLIP: container.querySelector(hiddenLineItemPropSelector),
})

const setContainerData = (container, data) => {
  const {
    key,
    currentQuantity,
    cartQuantity,
    inputMaximum,
    hiddenLineItemProperty,
  } = data

  const { multiplier } = getDataFrom(container)

  const { visibleInput, hiddenQuantity, hiddenLIP } = getInputElementsFrom(
    container
  )

  // Round value to nearest valid fraction
  const newValue = currentQuantity / multiplier

  if (key != null) {
    container.dataset.fsaCartItemKey = key
  }

  if (currentQuantity != null) {
    container.dataset.fsaCurrentQuantity = currentQuantity
    if (
      currentQuantity % multiplier === 0 &&
      currentQuantity > 0 &&
      multiplier > 1
    ) {
      visibleInput.value = newValue.toFixed(1)
    } else {
      visibleInput.value = newValue
    }
    hiddenQuantity.value = currentQuantity
  }

  if (cartQuantity != null) {
    container.dataset.fsaCartQuantity = cartQuantity
  }

  if (inputMaximum != null) {
    container.dataset.fsaMax = inputMaximum
  }

  const inCart = cartQuantity && cartQuantity !== 0
  if (inCart || cartQuantity || hiddenLineItemProperty) {
    // ignore lips until the checkout button is pressed.

    if (hiddenLineItemProperty && hiddenLIP) {
      hiddenLIP.value = hiddenLineItemProperty
    }
  } else if (hiddenLIP) {
    hiddenLIP.value = generateLineItemPropertyFromValue(newValue)
  }
}

const minus = (container) => {
  const { step, currentQuantity, minimum, maximum, cartQuantity } = getDataFrom(
    container
  )

  const { minusButton, plusButton } = getMinusPlusElementsFrom(container)

  let newQuantity = currentQuantity - step
  const nextPlusQuantity = currentQuantity
  const inCart = cartQuantity !== 0

  if (newQuantity <= 0) {
    newQuantity = minimum
  }

  setContainerData(container, {
    currentQuantity: newQuantity,
    cartQuantity,
  })

  if (nextPlusQuantity <= maximum) {
    magicEnable(plusButton)
  }

  if (inCart && newQuantity <= step) {
    magicDisable(minusButton)
  }

  if (!inCart && newQuantity <= minimum) {
    magicDisable(minusButton)
  }
}

const plus = (container) => {
  const {
    step,
    currentQuantity,
    minimum,
    maximum,
    cartQuantity,
    shopifyMaximumQuantity,
  } = getDataFrom(container)

  const { minusButton, plusButton } = getMinusPlusElementsFrom(container)

  const inCart = cartQuantity !== 0

  // The next minus value is the current value
  const nextMinusQuantity = currentQuantity

  // Get the Next Maximum Value to check that we don't exceed the shopify quantity
  // TESTING: comment out the disable check in liquid or results will not be accurate
  const newInputMaxQuantity = shopifyMaximumQuantity - cartQuantity

  // Set Next Value
  let newQuantity = currentQuantity + step

  if (newQuantity >= newInputMaxQuantity) {
    newQuantity = maximum
  }

  setContainerData(container, {
    currentQuantity: newQuantity,
    cartQuantity,
  })

  if (inCart && nextMinusQuantity > step) {
    magicEnable(minusButton)
  }

  if (!inCart && nextMinusQuantity <= minimum) {
    magicEnable(minusButton)
  }

  if (newQuantity >= newInputMaxQuantity) {
    magicDisable(plusButton)
  }
}

const type = (container) => {
  const {
    step,
    currentQuantity,
    minimum,
    maximum,
    multiplier,
    cartQuantity,
  } = getDataFrom(container)

  const { visibleInput, minusButton, plusButton } = getAllElementsFrom(
    container
  )

  const inCart = cartQuantity !== 0
  const inputQuantity = parseInt(
    parseFloat(visibleInput.value) * multiplier,
    10
  )
  let newQuantity = currentQuantity

  if (Number.isNaN(inputQuantity)) {
    newQuantity = currentQuantity
  } else {
    newQuantity = inputQuantity

    if (newQuantity % step !== 0) {
      newQuantity = Math.round(newQuantity).toFixed(2)
    }

    if (inCart && newQuantity < step) {
      newQuantity = step
    }

    if (!inCart && newQuantity < minimum) {
      newQuantity = minimum
    }

    if (newQuantity >= maximum) {
      newQuantity = maximum
    }

    if (newQuantity > minimum) {
      magicEnable(minusButton)
    }

    if (newQuantity < maximum) {
      magicEnable(plusButton)
    }

    if (newQuantity <= minimum) {
      magicDisable(minusButton)
    }

    if (newQuantity >= maximum) {
      magicDisable(plusButton)
    }
  }

  setContainerData(container, {
    currentQuantity: newQuantity,
    cartQuantity,
  })
}

const addToCart = (event) => {
  const form = event.target.closest("form[action='/cart/add']")

  const extraHidden = form.querySelectorAll('[name^="properties"]')

  const allLineItemProperties = {}
  extraHidden.forEach((element) => {
    allLineItemProperties[
      element.name.replace("properties", "").replace("[", "").replace("]", "")
    ] = element.value
  })

  const container = form.querySelector("[data-fsa-container-product]")

  const {
    variantId,
    itemKey,
    cartQuantity,
    multiplier,
    currentQuantity,
  } = getDataFrom(container)

  const quantity = currentQuantity + cartQuantity

  const inCart = cartQuantity !== 0

  if (!inCart) {
    ThemeCart.addItem(variantId, {
      quantity,
      properties: allLineItemProperties,
    }).then(() => {
      document.location.href = "/cart"
    })
  } else {
    allLineItemProperties["Cut Length"] = generateLineItemPropertyFromValue(
      quantity / multiplier
    )
    ThemeCart.updateItem(itemKey, {
      quantity,
      properties: allLineItemProperties,
    }).then(() => {
      document.location.href = "/cart"
    })
  }
}

const updateContainerWithItem = (container, item) => {
  const { step, multiplier, shopifyMaximumQuantity } = getDataFrom(container)

  const { plusButton, minusButton } = getMinusPlusElementsFrom(container)

  const newCartQuantity = parseInt(item.quantity, 10)
  const newMaxQuantity = shopifyMaximumQuantity - newCartQuantity

  let newInputQuantity

  if (newMaxQuantity >= multiplier) {
    newInputQuantity = multiplier
  } else {
    newInputQuantity = newMaxQuantity
  }

  let hiddenLineItemProperty = null
  if (item.properties && item.properties["Cut Length"]) {
    hiddenLineItemProperty = item.properties["Cut Length"]
  }

  setContainerData(container, {
    key: item.key,
    cartQuantity: newCartQuantity,
    currentQuantity: newInputQuantity,
    inputMaximum: newMaxQuantity,
    hiddenLineItemProperty,
  })

  if (newInputQuantity <= step) {
    magicDisable(minusButton)
  } else {
    magicEnable(minusButton)
  }

  if (newInputQuantity >= newMaxQuantity) {
    magicDisable(plusButton)
  } else {
    magicEnable(plusButton)
  }
}

const updateAfterAddToCart = (item) => {
  const containers = document.querySelectorAll(
    `[data-fsa-product-variant-id="${item.variant_id}"]`
  )
  if (containers) {
    containers.forEach((container) => {
      updateContainerWithItem(container, item)
    })
  }
}

const updateAfterLocalStorageChange = (cart) => {
  console.warn("[Update Product Container]")
  cart.items.forEach((item) => {
    const containers = document.querySelectorAll(
      `[data-fsa-product-variant-id="${item.variant_id}"]`
    )
    if (containers) {
      containers.forEach((container) => {
        updateContainerWithItem(container, item)
      })
    }
  })
}

export default {
  addToCartFormSelector,
  updateAfterAddToCart,
  addToCartSelector,
  addToCart,
  getDataFrom,
  getInputElementsFrom,
  getAllProductContainersByVariantId,
  containerVariantIdSelector,
  minusButtonSelector,
  visibleInputSelector,
  plusButtonSelector,
  hiddenQuantitySelector,
  hiddenLineItemPropSelector,
  minus,
  plus,
  type,
  updateAfterLocalStorageChange,
}
